import React, { useEffect, useState } from 'react';
import { ChevronRightIcon, FolderIcon, HomeIcon } from '@heroicons/react/24/outline';
import { docConvertAPI } from '../../api/docConvertApi';

/**
 * Klasör seçici — kök klasörden başlayarak alt klasörlere gezinilebilir.
 * Bir klasöre tıklamak onu hedef olarak seçer; ">" ile içine girilir.
 * value = seçili klasör id (null = klasörsüz/kök).
 */
export default function FolderPicker({ value, onChange, allowRoot = true }) {
  const [parentId, setParentId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (nextParent) => {
    setLoading(true);
    try {
      const resp = await docConvertAPI.getFolders(nextParent || undefined);
      setFolders(resp?.data || []);
      setBreadcrumb(resp?.breadcrumb || []);
      setParentId(nextParent ?? null);
    } catch {
      setFolders([]);
      setBreadcrumb([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(null);
  }, []);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-100 dark:border-gray-700 px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
        <button
          type="button"
          onClick={() => load(null)}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <HomeIcon className="h-3.5 w-3.5" /> Kök
        </button>
        {breadcrumb.map((crumb) => (
          <React.Fragment key={crumb.id}>
            <ChevronRightIcon className="h-3 w-3 text-gray-400" />
            <button
              type="button"
              onClick={() => load(crumb.id)}
              className="rounded px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="max-h-64 overflow-y-auto p-2">
        {allowRoot && parentId === null && (
          <button
            type="button"
            onClick={() => onChange(null, 'Klasörsüz (kök)')}
            className={`mb-1 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors ${
              value === null
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50'
            }`}
          >
            <HomeIcon className="h-4 w-4 shrink-0" />
            Klasörsüz (kök)
          </button>
        )}

        {loading ? (
          <p className="px-2 py-4 text-center text-xs text-gray-400">Yükleniyor…</p>
        ) : folders.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-gray-400">Bu konumda klasör yok.</p>
        ) : (
          folders.map((folder) => {
            const selected = value === folder.id;
            return (
              <div
                key={folder.id}
                className={`flex items-center gap-1 rounded-md ${
                  selected
                    ? 'bg-blue-50 dark:bg-blue-900/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onChange(folder.id, folder.name)}
                  className={`flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left text-sm ${
                    selected ? 'text-blue-700 dark:text-blue-200' : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <FolderIcon className="h-4 w-4 shrink-0 text-amber-500" />
                  <span className="truncate">{folder.name}</span>
                  <span className="ml-auto shrink-0 text-[10px] text-gray-400">
                    {folder.subfolder_count > 0 && `${folder.subfolder_count} klasör`}
                    {folder.subfolder_count > 0 && folder.note_count > 0 && ' · '}
                    {folder.note_count > 0 && `${folder.note_count} not`}
                  </span>
                </button>
                {folder.subfolder_count > 0 && (
                  <button
                    type="button"
                    onClick={() => load(folder.id)}
                    title="İçine gir"
                    className="mr-1 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-600"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
